import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { confirmSignIn, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import { Link } from 'react-router-dom';
import { getSigningApiUrl, getSigningAwsAccount, setLoginStorage } from '../amplify-config';
import LoadingPage from '../components/LoadingPage';
import { resolveNextOrDefault } from '../utils/safe-next';

/**
 * localStorage key used to hand off the "Stay signed in on this device"
 * preference across the magic-link page-load gap. Written by LoginPage when
 * the user submits the magic-link form; read here just before token exchange
 * so Amplify persists the issued tokens to the correct storage backend; then
 * cleared so it doesn't leak to subsequent unrelated sign-ins. (#1944)
 */
const REMEMBER_ME_STORAGE_KEY = 'login-remember-me';

/**
 * Reads the "Stay signed in" preference stashed by LoginPage from
 * localStorage. Returns `false` (privacy-respecting default) when the key is
 * absent (cross-browser magic-link open) or when storage access throws
 * (private mode). (#1944)
 */
function readRememberMePreference(): boolean {
    try {
        return window.localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
}

/**
 * Clears the "Stay signed in" preference handoff key so it doesn't leak to a
 * later, unrelated sign-in on the same device. (#1944)
 */
function clearRememberMePreference(): void {
    try {
        window.localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
    } catch {
        /* private mode — nothing to clear */
    }
}

/**
 * Computes a SHA-256 hex hash of the given string using the Web Crypto API.
 */
async function hashToken(token: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Handles the passwordless magic link login flow for signers.
 *
 * Flow:
 * 1. Extracts `:token` from URL params.
 * 2. Calls `GET /auth/login/:token` to resolve the token to userId/tenantId.
 * 3. If the call fails (404/410), shows an error.
 * 4. Checks if the user is already authenticated as the correct user.
 * 5. If not authenticated, initiates Cognito CUSTOM_AUTH with SHA-256 hashed token.
 * 6. On success, navigates to `/signer`.
 */
function LoginByToken() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [missingToken, setMissingToken] = useState(false);

    /**
     * Post-auth navigation target derived from the `?next=` query parameter
     * (issue #1477). The parameter is URL-encoded at the email-build site so
     * `?next=` values containing their own query strings round-trip cleanly.
     * `decodeURIComponent` may throw on malformed input — that case falls
     * back to the safe default along with every other rejection path.
     *
     * `resolveNextOrDefault` enforces the same-origin contract; see
     * `utils/safe-next.ts` for the full attack-vector list.
     */
    const nextTarget = useMemo(() => {
        const fallback = '/signer';
        const rawValue = searchParams.get('next');
        if (rawValue === null) return fallback;
        let decoded: string | null;
        try {
            decoded = decodeURIComponent(rawValue);
        } catch {
            decoded = null;
        }
        return resolveNextOrDefault(decoded, fallback);
    }, [searchParams]);

    /**
     * Performs the CUSTOM_AUTH Cognito flow using the raw token as the challenge response.
     * Handles "already signed in as different user" by signing out and retrying.
     *
     * Before calling signIn, swaps Amplify's token storage based on the
     * "Stay signed in on this device" preference stashed by LoginPage on the
     * /login page load. Defaults to sessionStorage (rememberMe=false) when
     * the key is absent — e.g., when the magic link is opened in a different
     * browser than the one that requested it. (#1944)
     */
    const performCustomAuth = useCallback(
        async (userId: string) => {
            const tokenHash = await hashToken(token!);
            const cognitoUsername = userId.toLowerCase();

            // Must run BEFORE signIn so the issued tokens land in the
            // storage backend the user chose on the /login page.
            const rememberMe = readRememberMePreference();
            setLoginStorage(rememberMe);

            const attemptSignIn = async () => {
                const result = await signIn({
                    username: cognitoUsername,
                    options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
                });

                if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE') {
                    const confirmResult = await confirmSignIn({ challengeResponse: tokenHash });
                    if (confirmResult.isSignedIn) {
                        // Successful sign-in — clear the handoff key so it
                        // doesn't leak to subsequent unrelated sign-ins.
                        clearRememberMePreference();
                        navigate(nextTarget, { replace: true });
                    }
                }
            };

            try {
                await attemptSignIn();
            } catch (err) {
                if (err instanceof Error && err.message.includes('There is already a signed in user')) {
                    await signOut();
                    await attemptSignIn();
                    return;
                }
                throw err;
            }
        },
        [token, navigate, nextTarget]
    );

    const resolveAndAuth = useCallback(async () => {
        if (!token) {
            setMissingToken(true);
            setLoading(false);
            return;
        }

        // Step 1: Resolve token → userId/tenantId
        let userId: string;
        try {
            const response = await fetch(`${getSigningApiUrl()}/auth/login/${token}`, {
                headers: { 'X-Aws-Account': getSigningAwsAccount() },
            });

            if (!response.ok) {
                if (response.status === 410) {
                    setError('This sign-in link has expired. Please request a new one.');
                } else {
                    setError('This sign-in link is invalid or has already been used.');
                }
                setLoading(false);
                return;
            }

            const data = (await response.json()) as { userId: string; tenantId: string };
            userId = data.userId;
        } catch {
            setError('Failed to load the sign-in link. Please check your connection and try again.');
            setLoading(false);
            return;
        }

        const cognitoUsername = userId.toLowerCase();

        // Step 2: Check if already authenticated as the correct user
        try {
            const currentUser = await getCurrentUser();
            if (currentUser.username === cognitoUsername) {
                // Already signed in as the correct user — clear the
                // preference key so it doesn't leak to a future sign-in
                // and navigate to the validated `?next=` target (or
                // `/signer` fallback). (#1944, #1477)
                clearRememberMePreference();
                navigate(nextTarget, { replace: true });
                return;
            }
            await signOut();
        } catch {
            // Not authenticated — proceed with CUSTOM_AUTH
        }

        // Step 3: CUSTOM_AUTH flow
        try {
            await performCustomAuth(userId);
        } catch {
            setError('Authentication failed. Your sign-in link may be invalid or expired. Please request a new one.');
        } finally {
            setLoading(false);
        }
    }, [token, navigate, performCustomAuth, nextTarget]);

    useEffect(() => {
        resolveAndAuth();
    }, [resolveAndAuth]);

    if (missingToken) {
        return (
            <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Invalid Sign-In Link
                    </Typography>
                    <Typography role="alert" aria-live="assertive" color="text.secondary" sx={{ mt: 1 }}>
                        The sign-in link is missing or invalid.
                    </Typography>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    if (loading) {
        return <LoadingPage message="Signing in..." />;
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', py: 4 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" component="h2" gutterBottom>
                        Unable to Sign In
                    </Typography>
                    <Alert severity="error" role="alert" aria-live="assertive" sx={{ mt: 1, textAlign: 'left' }}>
                        {error}
                    </Alert>
                    <Box sx={{ mt: 3 }}>
                        <Button component={Link} to="/login" variant="outlined">
                            Back to Login
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    return <LoadingPage message="Authenticating..." />;
}

export default LoginByToken;
